import React from 'react';

/**
 * A tiny per-card error boundary. Catches any render error in ONE optional
 * match-center card and renders nothing instead of letting it crash the whole
 * screen (the global ErrorBoundary's "Something went wrong" fallback). Built
 * from a bare class component with no UI of its own, so the guard itself can
 * never be the thing that fails.
 *
 * Wrap each new / optional card whose data is provider-shaped (and therefore not
 * schema-validated) in `<CardBoundary>...</CardBoundary>`. A broken card simply
 * disappears; the rest of the match screen keeps working.
 */
export class CardBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: unknown) {
    // Warn (not error) — a single optional card failing is recoverable and
    // shouldn't read as a fatal crash in logs.
    console.warn('[CardBoundary] suppressed a card render error', error);
  }

  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}
